import { Image, TouchableOpacity, View, ScrollView } from "react-native";
import { useThemedStyleFunction } from "../theme-provider";
import { ThemedText } from "../themed-text";
import { statusReportSliderStyle } from "./status-report-slider.style";

// Fallback image for reports without an image
const fallbackImage = require('../../assets/images/splash-bg.jpg');

export interface StatusReport {
    id: number;
    rating: number;
    comment?: string;
    imageUrl?: string;
    reportedAt: string;
    user?: {
        id: number;
        firstName?: string;
        lastName?: string;
    };
}

type StatusReportSliderProps = {
    reports: StatusReport[];
    onImagePress?: (report: StatusReport) => void;
};

const StatusReportSlider = ({reports, onImagePress}: StatusReportSliderProps) => {
    const style = useThemedStyleFunction(statusReportSliderStyle);

    if (!reports || reports.length === 0) {
        return null;
    }

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        if (date.toDateString() === today.toDateString()) {
            return 'Idag';
        } else if (date.toDateString() === yesterday.toDateString()) {
            return 'Igår';
        } else {
            const diffTime = Math.abs(today.getTime() - date.getTime());
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
            if (diffDays < 7) {
                return `${diffDays} dagar sedan`;
            } else {
                return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' });
            }
        }
    };

    const renderStars = (rating: number) => {
        return (
            <View style={style.starsContainer}>
                {[1, 2, 3, 4, 5].map((star) => (
                    <ThemedText 
                        key={star} 
                        style={[style.star, star <= rating && style.starFilled]}
                    >
                        ★
                    </ThemedText>
                ))}
            </View>
        );
    };

    return (
        <View style={style.container}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                {reports.map((report) => (
                    <TouchableOpacity
                        key={report.id}
                        style={style.reportCard}
                        onPress={() => onImagePress?.(report)}
                    >
                        <Image 
                            style={style.backgroundImage} 
                            source={report.imageUrl ? { uri: report.imageUrl } : fallbackImage}
                            onLoadStart={() => console.log('Loading image:', report.imageUrl || 'fallback')}
                            onLoad={() => console.log('Image loaded successfully:', report.imageUrl || 'fallback')}
                            onError={(e) => console.log('Failed to load image:', report.imageUrl, e.nativeEvent.error)}
                        />
                        
                        <View style={style.overlay}>
                            <View style={style.contentContainer}>
                                {renderStars(report.rating)}
                                <ThemedText style={style.date}>
                                    {formatDate(report.reportedAt)}
                                </ThemedText>
                            </View>
                        </View>
                    </TouchableOpacity>
                ))}
            </ScrollView>
        </View>
    );
};

export { StatusReportSlider };
